import React, {Component} from "react";
import TopNav from "../../../common/nav/TopNav";
import Footer from "../../../common/Footer";
import PageBlock from "../../../common/PageBlock";
import CodeBlock from "../../../common/CodeBlock";
import Upload from "../img/mocks/upload.png";
import Updating from "../img/mocks/updating.png";
import Ready from "../img/mocks/ready.png";
import Spec from "../img/mocks/spec.png";
import Error from "../img/mocks/error.png";
import {Link} from "react-router";

class GettingStarted extends Component {
    render() {
        return (
            <div>
                <TopNav tab="docs"/>

                <PageBlock>
                    <div className="container">

                        <div className="row">
                            <div className="col-lg-12">
                                <h1 className="page-header">Getting Started</h1>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-sm-12">
                                <p>
                                    In our fast moving and API driven world, teams often need to build an API’s client (mobile, web, service) alongside its
                                    implementation.
                                </p>
                                <p>
                                    Smart client developers might agree an API specification with the backend implementation developers. This enables both
                                    groups to work simultaneously, and hopefully in a relatively loosely coupled fashion.
                                </p>
                                <p>
                                    But when testing your client code, a specification document only gets you so far.
                                </p>
                                <blockquote>
                                    Wouldn’t it be better if your specification was also a live mock of the backend service? Say hello to CloudMocks.
                                </blockquote>
                                <h3>A simple specification</h3>
                                <p>
                                    Let’s start with a simple API. In keeping with the ancient traditions of our people, we will model a pet store. To keep
                                    things simple we’ll define a single endpoint for now, named <code>/pets</code> that will return a JSON array like so:
                                </p>
                                <CodeBlock code={`[
  {
    "id": 101,
    "name": "Cat"
  },
  {
    "id": 102,
    "name": "Dog"
  }
]
`}/>
                                <p>
                                    To represent this API, we are going to use <a href="https://github.com/OAI/OpenAPI-Specification" target="_blank">OpenAPI</a> (formerly
                                    called Swagger). This example uses version 2.0 of the specification, but you can use more recent versions too.
                                </p>
                                <h4>The specification</h4>
                                <p>
                                    Here is the OpenAPI specification for our endpoint:
                                </p>
                                <CodeBlock code={`---
swagger: "2.0"
info:
  version: "1.0.0"
  title: "Petstore"
consumes:
- "application/json"
produces:
- "application/json"
paths:
  /pets:
    get:
      description: "Returns all pets from the system"
      produces:
        - "application/json"
      responses:
        "200":
          description: "A list of pets."
          schema:
            type: "array"
            items:
              $ref: "#/definitions/Pet"
          examples:
            application/json: |-
              [
                {
                  "id": 101,
                  "name": "Cat"
                },
                {
                  "id": 102,
                  "name": "Dog"
                }
              ]
definitions:
  Pet:
    type: "object"
    required:
      - "id"
      - "name"
    properties:
      id:
        type: "integer"
        format: "int64"
      name:
        type: "string"
`}/>
                                <p>
                                    A few things to call out:
                                </p>
                                <ul>
                                    <li>We’ve defined the endpoint <code>/pets</code> as expecting an HTTP <code>GET</code> request</li>
                                    <li>We’ve said it will produce JSON responses</li>
                                    <li>One response is defined for the HTTP 200 case</li>
                                    <li>We’ve defined a basic data model in the <code>definitions</code> section — this is standard <a
                                        href="https://json-schema.org/" target="_blank">JSON Schema</a></li>
                                    <li>We’ve provided an example response — the same JSON array described earlier</li>
                                </ul>
                                <blockquote>
                                    Tip: There’s much, much more to OpenAPI than this. Check out the <a
                                    href="https://github.com/OAI/OpenAPI-Specification" target="_blank">specification</a> for more info.
                                </blockquote>
                                <h4>What’s next?</h4>
                                <p>
                                    On its own, the specification is a useful way to document and share your API. Let’s take it to the next level with a <a
                                    href="https://martinfowler.com/articles/mocksArentStubs.html" target="_blank">mock</a> service based on this specification that you can
                                    actually call from your client.
                                </p>
                                <h3>Creating a mock</h3>
                                <p>
                                    All we need to start our mock is to <Link to="/mocks/config">upload</Link> our API specification file. This tells CloudMocks
                                    to
                                    create a live mock.
                                </p>
                                <img src={Upload}
                                     alt="Screenshot of user uploading mock specification file into CloudMocks"
                                     className="img-responsive"
                                />
                                <p>
                                    Once you hit the <strong>Upload</strong> button, a new mock instance is created. This process can take a few minutes:
                                </p>
                                <img src={Updating}
                                     alt="Screenshot of mock updating"
                                     className="img-responsive"
                                />
                                <p>
                                    When your mock has been created successfully, you'll see a panel showing you a link to where your mock is running:
                                </p>
                                <img src={Ready}
                                     alt="Screenshot of successful mock"
                                     className="img-responsive"
                                />
                                <p>
                                    Congratulations! 🎉 You now have a working mock server. Let’s test it.
                                </p>
                                <h3>Testing your mock</h3>
                                <p>
                                    Let’s try a test from the command line:
                                </p>
                                <CodeBlock code={`$ curl -X GET "https://live.mocks.cloud/YOUR-MOCK-URL/pets"
[
  {
    "id": 101,
    "name": "Cat"
  },
  {
    "id": 102,
    "name": "Dog"
  }
]
`}/>
                                <p>
                                    <strong>It works!</strong> Your mock is returning the example response from your specification at the
                                    path <code>/pets</code> along with the <code>HTTP 200</code> status.
                                </p>
                                <h4>Exploring the mock</h4>
                                <p>
                                    To explore your mock, visit the UI by clicking the <strong>Open mock specification</strong> button.
                                    This will show you a page that looks like this:
                                </p>
                                <img src={Spec}
                                     alt="Screenshot of mock specification"
                                     className="img-responsive"
                                />
                                <h3> Summary </h3>
                                <p>
                                    You now have a simple mock server. Its endpoints are built automatically from your OpenAPI specification. This means
                                    that if you add further endpoints, or make changes, CloudMocks will mock those too.
                                </p>
                                <p>
                                    If you update your specification file, just upload the file to see the changes.
                                </p>
                                <h3>Customising your responses with scripts</h3>
                                <p>
                                    CloudMocks is simple to learn, but is also very customisable. If you outgrow returning static example responses from your
                                    specification, you can use scripts, written in JavaScript or Groovy, to control the response your mock returns.
                                </p>
                                <p>
                                    For more information on scripted responses, check out the <Link to="/docs/mocks/advanced">documentation</Link>.
                                </p>

                                {/* Troubleshooting section */}
                                <hr/>
                                <h3>Troubleshooting</h3>
                                <p>
                                    Sometimes CloudMocks can't create your mock and you will see an error message such as the following:
                                </p>
                                <img src={Error}
                                     alt="Screenshot of mock updating"
                                     className="img-responsive"
                                />
                                <p>
                                    Tips for diagnosing errors:
                                </p>
                                <ul>
                                    <li>Check your file is a valid <a
                                        href="https://github.com/OAI/OpenAPI-Specification" target="_blank">OpenAPI</a> 2.x or 3.0 specification
                                    </li>
                                    <li>Validate your specification file with a JSON/YAML validator</li>
                                    <li>If your specification is in YAML format, whitespace matters - ensure your indentation is correct and that you've haven't
                                        mixed spaces and tabs
                                    </li>
                                </ul>
                                <p>
                                    You can also see detailed information about the operation of your mocks in <Link to="/logs">the logs</Link>.
                                </p>
                            </div>
                        </div>

                    </div>
                </PageBlock>

                <Footer/>
            </div>
        );
    }
}

export default GettingStarted;



// WEBPACK FOOTER //
// ./src/modules/views/docs/mocks/GettingStarted.js